import type { PickupLocation } from '@otw/models'
import type {
  Contact,
  ContactActivity,
  ContactOwnerId,
  ContactStage,
  ContactStageEntry,
} from '@otw/models/leads/contacts'
import { resolveContactMarketingEmailStatus, resolveContactMarketingEmailSuppressed } from '@otw/models/leads/contacts'
import type {
  ContactCompanyMember,
  ContactCompanyRole,
} from '@otw/models/leads/contactCompanies'
import {
  appendContactStageHistory,
  getCurrentContactStage,
} from '@otw/models/leads/contactStageHistory'

export type MergeFieldSource = 'primary' | 'secondary'

export type MergeContactsFieldKey =
  | 'name'
  | 'email'
  | 'secondaryEmail'
  | 'phone'
  | 'birthday'
  | 'ownerId'
  | 'inventoryOwnerId'
  | 'stage'
  | 'inventoryStage'
  | 'userId'
  | 'dropoffLocation'

export type MergeContactsFieldSources = Record<MergeContactsFieldKey, MergeFieldSource>

export interface MergeContactsInput {
  primaryContactId: string
  secondaryContactId: string
  fieldSources: MergeContactsFieldSources
  /**
   * Explicit primary email chosen in the merge UI (from up to four candidates).
   * When provided (including empty string), overrides field-source picking for email.
   */
  primaryEmailOverride?: string
  /**
   * Explicit secondary email chosen/edited in the merge UI.
   * When provided (including empty string), overrides field-source picking
   * and the automatic “keep discarded primary email” fallback.
   */
  secondaryEmailOverride?: string
}

const STAGE_RANK: Record<ContactStage, number> = {
  customer: 0,
  opportunity: 1,
  prospect: 2,
  unqualified: 3,
}

const MEMBER_ROLE_RANK: Record<ContactCompanyRole, number> = {
  owner: 0,
  manager: 1,
  partner: 2,
}

const pickString = (
  primary: string | undefined,
  secondary: string | undefined,
  source: MergeFieldSource,
): string | undefined => {
  const primaryVal = primary?.trim() ?? ''
  const secondaryVal = secondary?.trim() ?? ''
  const chosen = source === 'primary' ? primaryVal : secondaryVal
  const fallback = source === 'primary' ? secondaryVal : primaryVal
  return chosen || fallback || undefined
}

const pickOwnerId = (
  primary: ContactOwnerId,
  secondary: ContactOwnerId,
  source: MergeFieldSource,
): ContactOwnerId => {
  const primarySet =
    primary !== null && primary !== undefined && String(primary).trim() !== ''
  const secondarySet =
    secondary !== null && secondary !== undefined && String(secondary).trim() !== ''
  if (source === 'primary') {
    return primarySet ? primary : secondarySet ? secondary : null
  }
  return secondarySet ? secondary : primarySet ? primary : null
}

const pickStage = (
  primary: ContactStage,
  secondary: ContactStage,
  source: MergeFieldSource,
): ContactStage => (source === 'primary' ? primary : secondary)

const pickDropoffLocation = (
  primary: PickupLocation | undefined,
  secondary: PickupLocation | undefined,
  source: MergeFieldSource,
): PickupLocation | undefined => {
  const chosen = source === 'primary' ? primary : secondary
  const fallback = source === 'primary' ? secondary : primary
  return chosen ?? fallback
}

const pickUserId = (
  primary: string | undefined,
  secondary: string | undefined,
  source: MergeFieldSource,
): string | undefined => {
  const primaryVal = primary?.trim() ?? ''
  const secondaryVal = secondary?.trim() ?? ''
  const chosen = source === 'primary' ? primaryVal : secondaryVal
  const fallback = source === 'primary' ? secondaryVal : primaryVal
  return chosen || fallback || undefined
}

export const mergeContactActivities = (
  primaryActivities: ContactActivity[],
  secondaryActivities: ContactActivity[],
): ContactActivity[] =>
  [...primaryActivities, ...secondaryActivities].sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
  )

export const mergeContactStageHistories = (
  primaryHistory: ContactStageEntry[] | undefined,
  secondaryHistory: ContactStageEntry[] | undefined,
): ContactStageEntry[] =>
  [...(primaryHistory ?? []), ...(secondaryHistory ?? [])].sort(
    (a, b) => a.changedAt.getTime() - b.changedAt.getTime(),
  )

export const mergeContactLeadIds = (primaryIds: string[], secondaryIds: string[]): string[] => [
  ...new Set([...primaryIds, ...secondaryIds]),
]

const maxActivityDate = (primary: Date | null, secondary: Date | null): Date | null => {
  const primaryMs = primary?.getTime() ?? -Infinity
  const secondaryMs = secondary?.getTime() ?? -Infinity
  const maxMs = Math.max(primaryMs, secondaryMs)
  return maxMs === -Infinity ? null : new Date(maxMs)
}

const collectContactEmails = (contact: Contact): string[] => {
  const emails: string[] = []
  const seen = new Set<string>()
  for (const raw of [contact.email, contact.secondaryEmail]) {
    const trimmed = raw?.trim() ?? ''
    if (!trimmed) continue
    const key = trimmed.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    emails.push(trimmed)
  }
  return emails
}

export const getMergeEmailCandidates = (
  primary: Contact,
  secondary: Contact,
): string[] => {
  const emails: string[] = []
  const seen = new Set<string>()
  for (const contact of [primary, secondary]) {
    for (const email of collectContactEmails(contact)) {
      const key = email.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      emails.push(email)
    }
  }
  return emails
}

export const getDefaultSecondaryEmailForMerge = (
  primary: Contact,
  secondary: Contact,
  primaryEmailSource: MergeFieldSource,
): string | undefined => {
  const chosenPrimary = pickString(primary.email, secondary.email, primaryEmailSource)
  const chosenNorm = chosenPrimary?.trim().toLowerCase() ?? ''
  const otherPrimary =
    primaryEmailSource === 'primary' ? secondary.email : primary.email
  const otherPrimaryTrim = otherPrimary?.trim() ?? ''
  if (otherPrimaryTrim && otherPrimaryTrim.toLowerCase() !== chosenNorm) {
    return otherPrimaryTrim
  }
  for (const candidate of getMergeEmailCandidates(primary, secondary)) {
    if (candidate.toLowerCase() !== chosenNorm) return candidate
  }
  return pickString(primary.secondaryEmail, secondary.secondaryEmail, 'primary')
}

export const buildMergedContact = (
  primary: Contact,
  secondary: Contact,
  fieldSources: MergeContactsFieldSources,
  secondaryEmailOverride?: string,
  primaryEmailOverride?: string,
): Contact => {
  const now = new Date()
  const mergedStage = pickStage(primary.stage, secondary.stage, fieldSources.stage)
  let stageHistory = mergeContactStageHistories(primary.stageHistory, secondary.stageHistory)
  const currentFromHistory = getCurrentContactStage(stageHistory, mergedStage)
  if (currentFromHistory !== mergedStage) {
    stageHistory = appendContactStageHistory(stageHistory, mergedStage, now)
  }

  const primaryInventoryStage = primary.inventoryStage ?? 'prospect'
  const secondaryInventoryStage = secondary.inventoryStage ?? 'prospect'
  const mergedInventoryStage = pickStage(
    primaryInventoryStage,
    secondaryInventoryStage,
    fieldSources.inventoryStage,
  )
  let inventoryStageHistory = mergeContactStageHistories(
    primary.inventoryStageHistory,
    secondary.inventoryStageHistory,
  )
  const currentInventoryFromHistory = getCurrentContactStage(
    inventoryStageHistory,
    mergedInventoryStage,
  )
  if (currentInventoryFromHistory !== mergedInventoryStage) {
    inventoryStageHistory = appendContactStageHistory(
      inventoryStageHistory,
      mergedInventoryStage,
      now,
    )
  }

  const merged: Contact = {
    ...primary,
    name: pickString(primary.name, secondary.name, fieldSources.name)?.trim() ?? primary.name.trim(),
    ownerId: pickOwnerId(primary.ownerId, secondary.ownerId, fieldSources.ownerId),
    inventoryOwnerId: pickOwnerId(
      primary.inventoryOwnerId ?? null,
      secondary.inventoryOwnerId ?? null,
      fieldSources.inventoryOwnerId,
    ),
    stage: getCurrentContactStage(stageHistory, mergedStage),
    stageHistory,
    inventoryStage: getCurrentContactStage(inventoryStageHistory, mergedInventoryStage),
    inventoryStageHistory,
    leadIds: mergeContactLeadIds(primary.leadIds ?? [], secondary.leadIds ?? []),
    activities: mergeContactActivities(primary.activities ?? [], secondary.activities ?? []),
    lastActivityAt: maxActivityDate(primary.lastActivityAt, secondary.lastActivityAt),
    marketingEmailStatus:
      resolveContactMarketingEmailStatus(primary) === 'unsubscribed' ||
      resolveContactMarketingEmailStatus(secondary) === 'unsubscribed'
        ? 'unsubscribed'
        : 'subscribed',
    marketingEmailSuppressed:
      resolveContactMarketingEmailSuppressed(primary) ||
      resolveContactMarketingEmailSuppressed(secondary),
    updatedAt: now,
  }

  const email =
    primaryEmailOverride !== undefined
      ? primaryEmailOverride.trim() || undefined
      : pickString(primary.email, secondary.email, fieldSources.email)
  if (email) merged.email = email
  else delete merged.email

  let secondaryEmail: string | undefined
  if (secondaryEmailOverride !== undefined) {
    secondaryEmail = secondaryEmailOverride.trim() || undefined
  } else {
    secondaryEmail = pickString(
      primary.secondaryEmail,
      secondary.secondaryEmail,
      fieldSources.secondaryEmail,
    )
    // Keep the discarded primary email when secondary is empty so merge does not drop it.
    if (email && !secondaryEmail?.trim()) {
      secondaryEmail = getDefaultSecondaryEmailForMerge(
        primary,
        secondary,
        fieldSources.email,
      )
    }
  }
  if (
    secondaryEmail &&
    email &&
    secondaryEmail.trim().toLowerCase() === email.trim().toLowerCase()
  ) {
    secondaryEmail = undefined
  }
  if (secondaryEmail) merged.secondaryEmail = secondaryEmail
  else delete merged.secondaryEmail

  const phone = pickString(primary.phone, secondary.phone, fieldSources.phone)
  if (phone) merged.phone = phone
  else delete merged.phone

  const birthday = pickString(primary.birthday, secondary.birthday, fieldSources.birthday)
  if (birthday) merged.birthday = birthday
  else delete merged.birthday

  const dropoffLocation = pickDropoffLocation(
    primary.dropoffLocation,
    secondary.dropoffLocation,
    fieldSources.dropoffLocation,
  )
  if (dropoffLocation) merged.dropoffLocation = dropoffLocation
  else delete merged.dropoffLocation

  const userId = pickUserId(primary.userId, secondary.userId, fieldSources.userId)
  if (userId) merged.userId = userId
  else delete merged.userId

  return merged
}

/** Rewire company membership when a duplicate contact record is merged away. */
export const remapCompanyMembersForContactMerge = (
  members: ContactCompanyMember[],
  secondaryContactId: string,
  primaryContactId: string,
): ContactCompanyMember[] => {
  if (secondaryContactId === primaryContactId) return members

  const secondaryMember = members.find((m) => m.contactId === secondaryContactId)
  if (!secondaryMember) return members

  const primaryMember = members.find((m) => m.contactId === primaryContactId)
  const withoutSecondary = members.filter((m) => m.contactId !== secondaryContactId)

  if (!primaryMember) {
    return [
      ...withoutSecondary,
      { ...secondaryMember, contactId: primaryContactId },
    ]
  }

  const keepSecondaryRole =
    MEMBER_ROLE_RANK[secondaryMember.role] < MEMBER_ROLE_RANK[primaryMember.role] ||
    (MEMBER_ROLE_RANK[secondaryMember.role] === MEMBER_ROLE_RANK[primaryMember.role] &&
      secondaryMember.addedAt.getTime() < primaryMember.addedAt.getTime())

  const mergedMember: ContactCompanyMember = keepSecondaryRole
    ? {
        ...secondaryMember,
        contactId: primaryContactId,
        addedAt:
          secondaryMember.addedAt.getTime() < primaryMember.addedAt.getTime()
            ? secondaryMember.addedAt
            : primaryMember.addedAt,
      }
    : {
        ...primaryMember,
        addedAt:
          primaryMember.addedAt.getTime() < secondaryMember.addedAt.getTime()
            ? primaryMember.addedAt
            : secondaryMember.addedAt,
      }

  return [
    ...withoutSecondary.filter((m) => m.contactId !== primaryContactId),
    mergedMember,
  ]
}

export const getDefaultMergeContactFieldSources = (
  primary: Contact,
  secondary: Contact,
): MergeContactsFieldSources => {
  const preferSource = (
    primaryVal: string | undefined | null,
    secondaryVal: string | undefined | null,
  ): MergeFieldSource => {
    const p = typeof primaryVal === 'string' ? primaryVal.trim() : ''
    const s = typeof secondaryVal === 'string' ? secondaryVal.trim() : ''
    if (p && !s) return 'primary'
    if (s && !p) return 'secondary'
    return 'primary'
  }

  const preferStageSource = (): MergeFieldSource => {
    if (STAGE_RANK[primary.stage] < STAGE_RANK[secondary.stage]) return 'primary'
    if (STAGE_RANK[secondary.stage] < STAGE_RANK[primary.stage]) return 'secondary'
    return 'primary'
  }

  const preferInventoryStageSource = (): MergeFieldSource => {
    const primaryInventory = primary.inventoryStage ?? 'prospect'
    const secondaryInventory = secondary.inventoryStage ?? 'prospect'
    if (STAGE_RANK[primaryInventory] < STAGE_RANK[secondaryInventory]) return 'primary'
    if (STAGE_RANK[secondaryInventory] < STAGE_RANK[primaryInventory]) return 'secondary'
    return 'primary'
  }

  const hasDropoff = (location: PickupLocation | undefined): boolean =>
    Boolean(
      location?.address?.trim() ||
        location?.city?.trim() ||
        location?.state?.trim() ||
        location?.zip?.trim(),
    )

  return {
    name: preferSource(primary.name, secondary.name),
    email: preferSource(primary.email, secondary.email),
    secondaryEmail: preferSource(primary.secondaryEmail, secondary.secondaryEmail),
    phone: preferSource(primary.phone, secondary.phone),
    birthday: preferSource(primary.birthday, secondary.birthday),
    ownerId: preferSource(
      primary.ownerId != null ? String(primary.ownerId) : '',
      secondary.ownerId != null ? String(secondary.ownerId) : '',
    ),
    inventoryOwnerId: preferSource(
      primary.inventoryOwnerId != null ? String(primary.inventoryOwnerId) : '',
      secondary.inventoryOwnerId != null ? String(secondary.inventoryOwnerId) : '',
    ),
    stage: preferStageSource(),
    inventoryStage: preferInventoryStageSource(),
    userId: preferSource(primary.userId, secondary.userId),
    dropoffLocation: preferSource(
      hasDropoff(primary.dropoffLocation) ? 'set' : '',
      hasDropoff(secondary.dropoffLocation) ? 'set' : '',
    ),
  }
}
